import { buildRequest, LineFramer, parseFrame, validateResult } from './codec.js';
import {
  MAX_LINE_BYTES,
  safeCopyForCode,
  type Command,
  type StatusResult,
  type WireEvent,
} from './protocol.js';

export const SUPPORTED_USB_FILTERS = Object.freeze([{ usbVendorId: 0x1A86, usbProductId: 0x55D3 }]);
const DEFAULT_TIMEOUT_MS = 6_000;
const STATUS_TIMEOUT_MS = 4_000;
const CLOSE_WAIT_MS = 1_500;
const CLOSE_PENDING_MESSAGE = 'El navegador todavía no ha liberado el puerto USB. Desconecta la placa; si persiste, cierra y vuelve a abrir ASIS antes de reconectar.';
const COMMAND_TIMEOUTS: Partial<Record<Command, number>> = { 'wifi.set': 60_000, pair: 45_000, 'sd.check': 10_000, 'camera.capture': 10_000, status: STATUS_TIMEOUT_MS };

export type ConnectionFailureStage = 'requesting' | 'validating' | 'opening' | 'initializing' | 'handshaking';
export type ConnectionFailureCause = 'SecurityError' | 'NotAllowedError' | 'NotFoundError' | 'NetworkError' | 'InvalidStateError' | 'AbortError' | 'UnknownError';
export interface ConnectionFailureDiagnostic { stage: ConnectionFailureStage; causeName: ConnectionFailureCause }
export type ConnectionState =
  | { kind: 'idle' } | { kind: 'requesting' } | { kind: 'opening' } | { kind: 'handshaking' }
  | { kind: 'connected'; status: StatusResult } | { kind: 'closed' } | { kind: 'failed'; reason: string; diagnostic?: ConnectionFailureDiagnostic };
export type StateObserver = (state: ConnectionState) => void;
export type EventListener = (event: WireEvent) => void;

export interface SerialPortLike {
  getInfo(): { usbVendorId?: number; usbProductId?: number };
  open(options: { baudRate: number }): Promise<void>;
  close(): Promise<void>;
  setSignals?(signals: { dataTerminalReady?: boolean; requestToSend?: boolean }): Promise<void>;
  readonly readable: ReadableStream<Uint8Array> | null;
  readonly writable: WritableStream<Uint8Array> | null;
  addEventListener(type: 'disconnect', listener: (event: Event) => void): void;
  removeEventListener(type: 'disconnect', listener: (event: Event) => void): void;
}
export interface NavigatorSerialLike { requestPort(options: { filters: Array<{ usbVendorId: number; usbProductId: number }> }): Promise<SerialPortLike> }
export interface DeviceUsbTransportOptions {
  navigatorSerial: NavigatorSerialLike;
  commandTimeoutMs?: number;
  statusTimeoutMs?: number;
  handshakeDelayMs?: number;
  idFactory?: () => string;
}

interface QueuedRequest {
  cmd: Command;
  fields: Record<string, unknown>;
  responseFields?: Record<string, unknown>;
  resolve(value: unknown): void;
  reject(error: Error): void;
  id?: string;
  timeout: number;
  timer?: ReturnType<typeof setTimeout>;
}

export class DeviceUsbCommandError extends Error {
  constructor(readonly code: string, message: string) { super(message); this.name = 'DeviceUsbCommandError'; }
}
export class DeviceUsbCancelledError extends Error {
  constructor() { super('Conexión USB cancelada.'); this.name = 'DeviceUsbCancelledError'; }
}
export class DeviceUsbConnectionError extends DeviceUsbCommandError {
  constructor(code: string, message: string, readonly stage: ConnectionFailureStage, readonly causeName: ConnectionFailureCause) {
    super(code, message);
    this.name = 'DeviceUsbConnectionError';
  }
}

function connectionError(error: unknown, stage: ConnectionFailureStage): DeviceUsbCommandError {
  if (error instanceof DeviceUsbCommandError) return error;
  // Copy only an allowlisted name. Browser errors can contain device paths and IDs;
  // never retain their message, stack, arbitrary fields or original cause.
  let causeName: ConnectionFailureCause = 'UnknownError';
  try {
    const name = (error as { name?: unknown } | null)?.name;
    if (name === 'SecurityError' || name === 'NotAllowedError' || name === 'NotFoundError' || name === 'NetworkError' || name === 'InvalidStateError' || name === 'AbortError') causeName = name;
  } catch { }
  const step = { requesting: 'seleccionar la placa', validating: 'comprobar la placa', opening: 'abrir el puerto', initializing: 'preparar el puerto', handshaking: 'comunicar con la placa' }[stage];
  if (causeName === 'SecurityError' || causeName === 'NotAllowedError') {
    return new DeviceUsbConnectionError('usb_permission_denied', `ASIS no ha obtenido permiso para ${step}. Pon ASIS en primer plano y vuelve a pulsar «Conectar por USB».`, stage, causeName);
  }
  if (stage === 'requesting' && causeName === 'NotFoundError') {
    return new DeviceUsbConnectionError('usb_selection_unavailable', 'No se ha seleccionado una placa USB compatible. Comprueba que está conectada y vuelve a pulsar «Conectar por USB».', stage, causeName);
  }
  if (stage === 'opening' && (causeName === 'NetworkError' || causeName === 'InvalidStateError')) {
    return new DeviceUsbConnectionError('usb_port_open_failed', 'No se pudo abrir el puerto USB. Cierra otros programas o monitores que lo estén usando y vuelve a conectar.', stage, causeName);
  }
  return new DeviceUsbConnectionError('usb_connection_failed', `No se pudo ${step} por USB. Desconecta y vuelve a conectar la placa.`, stage, causeName);
}

export class DeviceUsbTransport {
  private state: ConnectionState = { kind: 'idle' };
  private port: SerialPortLike | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private readonly framer = new LineFramer();
  private decoder = new TextDecoder('utf-8');
  private readonly observers = new Set<StateObserver>();
  private readonly eventListeners = new Set<EventListener>();
  private readonly queue: QueuedRequest[] = [];
  private pending: QueuedRequest | null = null;
  private readerActive = false;
  private destroyed = false;
  private connectAttempt: { epoch: number; cancel(): void } | null = null;
  private portOperation: Promise<void> | null = null;
  private epoch = 0;
  private closing: Promise<boolean> | null = null;
  private disconnectHandler: ((event: Event) => void) | null = null;
  private readonly options: Required<Pick<DeviceUsbTransportOptions, 'statusTimeoutMs'>> & DeviceUsbTransportOptions;

  constructor(options: DeviceUsbTransportOptions) {
    if (!options.navigatorSerial) throw new Error('navigator_serial_unavailable');
    this.options = { ...options, statusTimeoutMs: options.statusTimeoutMs ?? STATUS_TIMEOUT_MS };
  }

  getState(): ConnectionState { return this.state; }
  isConnected(): boolean { return this.state.kind === 'connected'; }
  getDeviceId(): string | null { return this.state.kind === 'connected' ? this.state.status.deviceId : null; }
  onState(observer: StateObserver): () => void { this.observers.add(observer); observer(this.state); return () => this.observers.delete(observer); }
  onEvent(listener: EventListener): () => void { this.eventListeners.add(listener); return () => this.eventListeners.delete(listener); }

  async connect(): Promise<StatusResult> {
    if (this.destroyed) throw new DeviceUsbCancelledError();
    if (this.closing || this.connectAttempt || this.port || this.state.kind === 'connected') throw new DeviceUsbCommandError('busy', 'La conexión USB ya está activa o cerrándose.');
    const epoch = ++this.epoch;
    let cancel!: () => void;
    const cancelled = new Promise<never>((_resolve, reject) => { cancel = () => reject(new DeviceUsbCancelledError()); });
    // A browser method can throw before wait() attaches its rejection handler.
    void cancelled.catch(() => {});
    // Browser operations can outlive cancellation. Their resource stays owned
    // until cleanup completes; this race only releases the caller/UI.
    const wait = <T>(operation: Promise<T>): Promise<T> => Promise.race([operation, cancelled]);
    const attempt = { epoch, cancel };
    this.connectAttempt = attempt;
    let stage: ConnectionFailureStage = 'requesting';
    try {
      this.setState({ kind: 'requesting' });
      const requested = await wait(this.options.navigatorSerial.requestPort({ filters: [...SUPPORTED_USB_FILTERS] }));
      this.assertEpoch(epoch);
      stage = 'validating';
      const info = requested.getInfo();
      if (info.usbVendorId !== 0x1A86 || info.usbProductId !== 0x55D3) throw new DeviceUsbCommandError('unsupported', 'El puerto seleccionado no corresponde a la placa ASIS.');
      stage = 'opening';
      this.setState({ kind: 'opening' });
      this.assertEpoch(epoch);
      this.port = requested;
      const operation = (async () => {
        await requested.open({ baudRate: 115_200 });
        this.assertEpoch(epoch);
        stage = 'initializing';
        try { await requested.setSignals?.({ dataTerminalReady: false, requestToSend: false }); } catch { }
        this.assertEpoch(epoch);
      })();
      this.portOperation = operation;
      await wait(operation);
      if (this.portOperation === operation) this.portOperation = null;
      this.disconnectHandler = () => { if (epoch === this.epoch && requested === this.port) void this.disconnect(); };
      requested.addEventListener('disconnect', this.disconnectHandler);
      if (!requested.readable || !requested.writable) throw new DeviceUsbCommandError('not_ready', 'Puerto serie no disponible.');
      this.reader = requested.readable.getReader();
      this.writer = requested.writable.getWriter();
      this.decoder = new TextDecoder('utf-8');
      this.framer.reset();
      this.readerActive = true;
      void this.readLoop(epoch);
      stage = 'handshaking';
      this.setState({ kind: 'handshaking' });
      // CH343 can reset the board on open even with DTR/RTS disabled.
      const delay = this.options.handshakeDelayMs ?? 800;
      if (delay > 0) await wait(new Promise(resolve => setTimeout(resolve, delay)));
      this.assertEpoch(epoch);
      let status: StatusResult;
      try { status = await wait(this.enqueue<StatusResult>('status', {}, epoch, this.options.statusTimeoutMs)); }
      catch (error) {
        this.assertEpoch(epoch);
        if (!(error instanceof DeviceUsbCommandError) || error.code !== 'timeout') throw error;
        // Only this read-only handshake is retryable; mutations are never replayed.
        status = await wait(this.enqueue<StatusResult>('status', {}, epoch, this.options.statusTimeoutMs));
      }
      if (status.protocol !== 1) throw new DeviceUsbCommandError('unsupported', 'Firmware no compatible con ASIS.');
      this.assertEpoch(epoch);
      this.setState({ kind: 'connected', status });
      return status;
    } catch (error) {
      if (epoch !== this.epoch || this.destroyed) throw new DeviceUsbCancelledError();
      const failure = connectionError(error, stage);
      const diagnostic = failure instanceof DeviceUsbConnectionError ? { diagnostic: { stage: failure.stage, causeName: failure.causeName } } : {};
      // Publish the real failure before waiting on browser teardown.
      this.setState({ kind: 'failed', reason: failure.message, ...diagnostic });
      const closed = await this.cleanup(new DeviceUsbCancelledError());
      if (epoch !== this.epoch || this.destroyed) throw new DeviceUsbCancelledError();
      if (!closed) this.setState({ kind: 'failed', reason: `${failure.message} ${CLOSE_PENDING_MESSAGE}`, ...diagnostic });
      throw failure;
    } finally {
      if (this.connectAttempt === attempt) this.connectAttempt = null;
    }
  }

  send<T = unknown>(cmd: Command, fields: Record<string, unknown> = {}): Promise<T> {
    if (this.destroyed) return Promise.reject(new DeviceUsbCancelledError());
    if (this.state.kind !== 'connected' || this.closing) return Promise.reject(new DeviceUsbCommandError('not_ready', 'La conexión USB no está lista.'));
    return this.enqueue<T>(cmd, fields, this.epoch, this.options.commandTimeoutMs ?? COMMAND_TIMEOUTS[cmd] ?? DEFAULT_TIMEOUT_MS);
  }

  async disconnect(): Promise<void> {
    const epoch = ++this.epoch;
    this.connectAttempt?.cancel();
    this.connectAttempt = null;
    const closed = await this.cleanup(new DeviceUsbCancelledError());
    if (!this.destroyed && epoch === this.epoch) this.setState(closed ? { kind: 'closed' } : { kind: 'failed', reason: CLOSE_PENDING_MESSAGE });
  }
  async destroy(): Promise<void> {
    if (this.destroyed) return;
    this.destroyed = true;
    this.epoch++;
    this.connectAttempt?.cancel();
    this.connectAttempt = null;
    const closed = await this.cleanup(new DeviceUsbCancelledError());
    this.observers.clear();
    this.eventListeners.clear();
    this.state = closed ? { kind: 'closed' } : { kind: 'failed', reason: CLOSE_PENDING_MESSAGE };
  }

  private assertEpoch(epoch: number): void {
    if (this.destroyed || epoch !== this.epoch) throw new DeviceUsbCancelledError();
  }

  private enqueue<T>(cmd: Command, fields: Record<string, unknown>, epoch: number, timeout: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const item: QueuedRequest = { cmd, fields, timeout, resolve: value => resolve(value as T), reject };
      this.queue.push(item);
      this.pumpQueue(epoch);
    });
  }

  private pumpQueue(epoch = this.epoch): void {
    if (epoch !== this.epoch || this.pending || !this.queue.length || !this.writer) return;
    const item = this.queue.shift()!;
    this.pending = item;
    item.id = this.options.idFactory?.() ?? createId();
    try {
      this.assertEpoch(epoch);
      const request = buildRequest({ id: item.id, cmd: item.cmd, fields: item.fields });
      if (new TextEncoder().encode(request.slice(0, -1)).length > MAX_LINE_BYTES) throw new DeviceUsbCommandError('bad_field', 'Petición demasiado larga.');
      item.responseFields = item.cmd === 'camera.capture' ? { target: item.fields.target } : {};
      item.fields = {};
      item.timer = setTimeout(() => {
        if (this.pending !== item) return;
        this.pending = null;
        item.reject(new DeviceUsbCommandError('timeout', 'La petición al dispositivo ha caducado.'));
        this.pumpQueue(epoch);
      }, item.timeout);
      void this.writer.write(new TextEncoder().encode(request)).catch(() => {
        if (this.pending === item) {
          this.pending = null;
          clearTimeout(item.timer);
          item.reject(new DeviceUsbCommandError('io', 'No se pudo enviar la petición al dispositivo.'));
          this.pumpQueue(epoch);
        }
      });
    } catch (error) {
      this.pending = null;
      clearTimeout(item.timer);
      item.reject(error instanceof Error ? error : new DeviceUsbCommandError('io', 'No se pudo enviar la petición al dispositivo.'));
      this.pumpQueue(epoch);
    }
  }

  private async readLoop(epoch: number): Promise<void> {
    const reader = this.reader;
    if (!reader) return;
    try {
      while (this.readerActive && epoch === this.epoch && reader === this.reader) {
        const { value, done } = await reader.read();
        if (epoch !== this.epoch || !this.readerActive || reader !== this.reader) return;
        if (done) break;
        if (value) {
          const text = this.decoder.decode(value, { stream: true });
          for (const line of this.framer.push(text)) this.handleLine(line, epoch);
        }
      }
    } catch {
      if (this.readerActive && epoch === this.epoch && reader === this.reader) void this.disconnect();
      return;
    }
    if (this.readerActive && epoch === this.epoch) void this.disconnect();
  }

  private handleLine(line: string, epoch: number): void {
    const frame = parseFrame(line);
    if (!frame || frame.kind === 'unknown' || epoch !== this.epoch) return;
    if (frame.kind === 'event') {
      for (const listener of this.eventListeners) { try { listener(frame.event); } catch { } }
      return;
    }
    const item = this.pending;
    if (!item || item.id !== frame.envelope.id) return;
    this.pending = null;
    clearTimeout(item.timer);
    if (frame.envelope.ok) {
      try {
        const result = validateResult(item.cmd, frame.envelope.result, item.responseFields);
        if (item.cmd === 'status' && this.state.kind === 'connected') this.setState({ kind: 'connected', status: result as unknown as StatusResult });
        item.resolve(result);
      } catch { item.reject(new DeviceUsbCommandError('bad_response', 'Respuesta del dispositivo no válida.')); }
    } else item.reject(new DeviceUsbCommandError(frame.envelope.error.code, safeCopyForCode(frame.envelope.error.code)));
    this.pumpQueue(epoch);
  }

  private cleanup(error: Error): Promise<boolean> {
    if (this.closing) return waitForClose(this.closing);
    this.readerActive = false;
    for (const item of this.queue.splice(0)) { item.fields = {}; clearTimeout(item.timer); item.reject(error); }
    if (this.pending) { this.pending.fields = {}; clearTimeout(this.pending.timer); this.pending.reject(error); this.pending = null; }
    const reader = this.reader;
    const writer = this.writer;
    const port = this.port;
    const operation = this.portOperation;
    const disconnectHandler = this.disconnectHandler;
    this.disconnectHandler = null;
    if (port && disconnectHandler) port.removeEventListener('disconnect', disconnectHandler);
    this.framer.reset();
    this.decoder = new TextDecoder('utf-8');
    const closing = (async (): Promise<boolean> => {
      // Wait for open/setSignals before closing: late completion must not revive
      // the port after a new connection has been started.
      if (operation) { try { await operation; } catch { } }
      if (this.portOperation === operation) this.portOperation = null;
      if (reader) {
        try { await reader.cancel(); } catch { }
        try { reader.releaseLock(); } catch { return false; }
        if (this.reader === reader) this.reader = null;
      }
      if (writer) {
        try { writer.releaseLock(); } catch { return false; }
        if (this.writer === writer) this.writer = null;
      }
      if (port) {
        try { await port.close(); } catch (error) { if (!isAlreadyClosed(port, error)) return false; }
        if (this.port === port) this.port = null;
      }
      return true;
    })().finally(() => { if (this.closing === closing) this.closing = null; });
    // Keep ownership and block reconnect until the real operations settle, even
    // after the UI wait expires. An explicit disconnect can retry a rejected
    // close without losing the still-owned port handle.
    this.closing = closing;
    return waitForClose(closing);
  }

  private setState(next: ConnectionState): void {
    if (this.destroyed && next.kind !== 'closed') return;
    this.state = next;
    for (const observer of this.observers) { try { observer(next); } catch { } }
  }
}

function createId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return `r${Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')}`;
}
function waitForClose(operation: Promise<boolean>): Promise<boolean> {
  return new Promise(resolve => {
    const timer = setTimeout(() => resolve(false), CLOSE_WAIT_MS);
    void operation.then(closed => { clearTimeout(timer); resolve(closed); }, () => { clearTimeout(timer); resolve(false); });
  });
}
function isAlreadyClosed(port: SerialPortLike, error: unknown): boolean {
  // Web Serial rejects close on an already closed port. Do not confuse that
  // with locked/open streams, an outstanding open, or any other close failure.
  try { return (error as { name?: unknown } | null)?.name === 'InvalidStateError' && port.readable === null && port.writable === null; }
  catch { return false; }
}
export { safeCopyForCode };
